import { date } from '@ipfabric/utils';
import type { RuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import { isMatch, isValid, parse } from 'date-fns';

const getParsedDate = (dateString: string, dateFormat: string): Date =>
  parse(
    dateString,
    dateFormat,
    // the default time will be 00:00:00 if there is not provided time in dateString
    new Date(0),
  );

const isValidDate = (parsedDate: Date): boolean =>
  isValid(parsedDate) && parsedDate.getTime() > 0;

export const getDateForFilter = (
  dateString: string,
  ruleContext: RuleContext,
): Date | null => {
  // Try dateFormat first (e.g., 'yyyy-MM-dd')
  // When the input date string uses the short 'yyyy-MM-dd' format (i.e., it contains no time or timezone component),
  // we treat this as a UTC midnight date. This is necessary because on the server, such dates are created as UTC midnight,
  // and this approach ensures correct behavior for equality operator filter comparisons (e.g., filtering for "2024-06-01" will
  // properly match a UTC midnight date created on the backend). For other date formats (those containing time and/or timezone information),
  // we parse the full time and timezone information from the string before converting to a UTC timestamp, ensuring filter comparisons
  // use the intended moment in time.
  if (isMatch(dateString, ruleContext.dateFormat)) {
    // convert dateString in the short 'yyyy-MM-dd' format to UTC midnight date
    const utcMidnightDate = new Date(dateString);
    // backend doesn't support dates before 1970
    if (utcMidnightDate.getTime() > 0) {
      return utcMidnightDate;
    }
    return null;
  }

  const parsedDate = getParsedDate(dateString, ruleContext.dateFormat);
  if (isValid(parsedDate)) {
    const shiftedDate = date.userTimeZoneToUtc(
      parsedDate.getTime(),
      ruleContext.timezone,
    );
    // backend doesn't support dates before 1970
    if (shiftedDate.getTime() > 0) {
      return shiftedDate;
    }
    return null;
  }

  // Try dateTimeFormat (e.g., 'yyyy-MM-dd, HH:mm:ss X')
  const parsedDateTime = getParsedDate(dateString, ruleContext.dateTimeFormat);
  if (isValidDate(parsedDateTime)) {
    return parsedDateTime;
  }

  // Try isoFormat (e.g., "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
  const parsedIsoDateTime = getParsedDate(dateString, ruleContext.isoFormat);
  if (isValidDate(parsedIsoDateTime)) {
    return parsedIsoDateTime;
  }

  return null;
};
